import styled from '@emotion/styled';
import { Alert, Col, Row, Spin } from 'antd';
import { useMemo } from 'react';
import { useAsync } from 'react-use';
import { useTranslate } from '@tolgee/react';
import { useCreateTrigger, useTriggerMapValue } from 'hooks/business/useTrigger';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { useAuthorization } from 'components/Authorization';
import { getReview } from 'services/api/dashboard';
import ExtraNode from '../Filter/ExtraNode';
import FilterProvider from '../Filter/Provider';
import { useDashboardFilter, useDashboardPlatformInfo } from '../Filter/useFilter';

import {
  last7days,
  momentDateRange2FetchParams,
  platformAll2Undefined,
  storeType2FetchParams,
} from '../../utils';
import TrendDrawer from '../Trend';
import TrendProvider from '../Trend/TrendProvider';
import BuyerComment from './BuyerComment';
import convertData from './convertData';
import Recover from './Recover';
import Reminder from './Reminder';
import Reply from './Reply';
import { chunk, compact } from 'lodash-es';

const Container = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  background-color: #ffffff;
  .ant-empty-footer {
    margin-top: 0;
  }

  .ant-card-head {
    .ant-card-head-title {
      .before {
        height: 14px;
        border-left: 4px solid #3366ff;
        display: inline-block;
        margin-right: 4px;
        margin-bottom: -1px;
      }
    }
  }
  .row {
    height: auto;
    .ant-card-body {
      height: 280px;
    }
    .ant-card-body > section {
      height: 232px;
    }
    .ant-col {
      .ant-card {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
      }
    }
  }
  .row + .row {
    margin-top: 16px;
  }
`;

const initialValue = {
  storeInfo: {},
  dateRange: last7days(),
} as const;

const Content = () => {
  const filterCtx = useDashboardFilter();
  const platform = platformAll2Undefined(filterCtx.platform);
  const fetchParams: Dashboard.BasicParams = useMemo(() => {
    const list = filterCtx.storeInfo.list || [];
    return {
      ...momentDateRange2FetchParams(filterCtx.dateRange),
      platform,
      storeType: storeType2FetchParams(filterCtx.storeType),
      storeIds: list.map((x) => x.id),
      departmentIds: filterCtx.departments?.map((_) => _.id),
    };
  }, [
    platform,
    filterCtx.dateRange,
    filterCtx.storeType,
    filterCtx.storeInfo.list,
    filterCtx.departments,
  ]);
  const { value, loading } = useAsync(() => {
    return getReview(fetchParams).then(convertData);
  }, [fetchParams]);

  const {
    platformInfo: { isOnlyMercado },
  } = useDashboardPlatformInfo();

  const modules = [
    <BuyerComment value={value} />,
    !isOnlyMercado && <Reply value={value} />,
    <Reminder value={value} />,
    <Recover value={value} />,
  ];

  return (
    <Spin spinning={loading}>
      <Container>
        {chunk(compact(modules), 2).map((modules) => {
          return (
            <Row className="row" gutter={[16, 16]}>
              {modules.map((module) => {
                return <Col span={12}>{module}</Col>;
              })}
            </Row>
          );
        })}
      </Container>
    </Spin>
  );
};

const Tips = () => {
  const { t } = useTranslate();

  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const { orgId = 0 } = payload || {};

  const permission = useGetPermission()('履约数据', { platform: 'LAZADA' });

  const { platform } = useDashboardFilter();

  const { DASHBOARD_LAZADA_REVIEW_TIPS } = useTriggerMapValue();

  const createTrigger = useCreateTrigger();

  const display =
    orgId < 1407 &&
    permission &&
    !DASHBOARD_LAZADA_REVIEW_TIPS &&
    (platform === undefined || platform === 'ALL' || platform === 'LAZADA');

  if (!display) {
    return <></>;
  }

  return (
    <Alert
      banner
      closable
      type="warning"
      message={t('review.reviewDashboardTips')}
      onClose={() => createTrigger({ trigger: 'DASHBOARD_LAZADA_REVIEW_TIPS' })}
      style={{ margin: '-16px 0 16px' }}
    />
  );
};

const Review = () => {
  return (
    <FilterProvider
      initialValue={initialValue}
      rightExtra={ExtraNode}
      dashboardName="Review"
      scene="ORDER_STAT"
      showPlatformAll
    >
      <TrendProvider>
        <Tips />
        <Content />
        <TrendDrawer />
      </TrendProvider>
    </FilterProvider>
  );
};

export default Review;
